import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Layout } from '@/components/Layout/Layout';
import { MainHeading } from '@/components/common/MainHeading';
import { SectionCard } from '@/components/common/SectionCard';
import { DetailItem } from '@/components/common/DetailItem';
import { StatusBadge } from '@/components/common/StatusBadge';
import { NoDataFound } from '@/components/common/NoDataFound';
import Button from '@/components/common/Button';
import ConfirmationModal from '@/components/common/ConfirmationModal';
import { callApi, ThrowError } from '@/api/callApi';
import { API_ENDPOINT, API_URL } from '@/api/constant';
import { IReturnOrder, ReturnStatus } from '@/interfaces/ReturnOrders/returnOrder.interface';
import { AssignDriverModal } from '@/components/common/Model/AssignDriverModal';
import { ProgressTracker } from '@/components/common/ProgressTracker';
import { DetailsGallery } from '@/components/common/DetailsGallery';
import { ProcessRefundModal } from '@/components/common/Model/ProcessRefundModal';
import {
    formatDateTime,
    formatRand,
    showToastNotification,
    parseOrderAddress,
    getReturnStatusIndex,
    returnSteps,
    formatStatus,
} from '@/utility/helper';
import {
    User,
    Phone,
    Truck,
    MapPin,
    Calendar,
    Hash,
    Wallet,
    Info,
    CheckCircle2,
    Package,
    Shield,
    Store,
    ShoppingBag,
    Mail,
    XCircle,
    Lock,
    Tag
} from 'lucide-react';
import { usePermissions } from '@/hooks/usePermissions';
import { RESOURCES } from '@/context/permissions';
import { Checkbox } from '@/components/ui/checkbox';

export const ReturnDetails = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { canEdit } = usePermissions();
    const [returnRequest, setReturnRequest] = useState<IReturnOrder | null>(null);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    // Refund calculation state
    const [selectedReturnItems, setSelectedReturnItems] = useState<string[]>([]);
    const [calculatingRefund, setCalculatingRefund] = useState(false);

    // Assign Driver Modal state
    const [showAssignModal, setShowAssignModal] = useState(false);

    // Rejection reason modal state
    const [showRejectModal, setShowRejectModal] = useState(false);
    const [rejectionReason, setRejectionReason] = useState('');
    const [showRefundModal, setShowRefundModal] = useState(false);
    const [verificationOtp, setVerificationOtp] = useState('');

    // const handleSelectAllReturnItems = (data: any) => {
    //     if (data?.returnItems) {
    //         setSelectedReturnItems(data.returnItems.map((item: any) => item.id));
    //     }
    // };

    const fetchReturnRequest = async () => {
        if (!id) return;
        setLoading(true);
        try {
            const response = await callApi<any>(
                `${API_URL}${API_ENDPOINT.return}/${id}`,
                { method: 'GET' }
            );
            const data = response?.data || null;
            setReturnRequest(data);
            // handleSelectAllReturnItems(data);
        } catch (error) {
            ThrowError(error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchReturnRequest();
    }, [id]);

    const handleCalculateRefund = async () => {
        if (!returnRequest || selectedReturnItems.length === 0) return;
        setCalculatingRefund(true);
        try {
            const response = await callApi<any>(
                `${API_URL}${API_ENDPOINT.return}/calculate-Return`,
                {
                    method: 'POST',
                    data: { returnItems: selectedReturnItems, requestId: id }
                }
            );

            if (response?.data?.refundAmount) {
                setReturnRequest(prev => prev ? { ...prev, refundAmount: response.data.refundAmount } : null);
            }
            // fetchReturnRequest();
            showToastNotification('success', 'Refund amount calculated successfully');
        } catch (error) {
            ThrowError(error);
        } finally {
            setCalculatingRefund(false);
        }
    };

    const handleStatusUpdate = async (newStatus: ReturnStatus, extraData: Record<string, any> = {}) => {
        if (!returnRequest) return;
        setActionLoading(true);
        try {
            let payload: Record<string, any>;


            const isPickup = newStatus === ReturnStatus.PICKUP_ASSIGNED;
            payload = {
                status: newStatus,
                ...extraData
            };


            await callApi(
                `${API_URL}${API_ENDPOINT.return}/${returnRequest.id}`,
                { method: 'PATCH', data: payload }
            );

            showToastNotification('success', `Return request status updated to ${newStatus.replace('_', ' ')}`);
            fetchReturnRequest();
        } catch (error) {
            ThrowError(error);
        } finally {
            setActionLoading(false);
            setShowRejectModal(false);
            setShowRefundModal(false);
            setVerificationOtp('');
        }
    };


    if (loading) {
        return (
            <Layout>
                <div className="flex items-center justify-center h-[60vh]">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
                </div>
            </Layout>
        );
    }

    if (!returnRequest) {
        return (
            <Layout>
                <NoDataFound
                    title="Return Request Not Found"
                    description="The return request details might have been removed or does not exist."
                    onAction={() => navigate(-1)}
                />
            </Layout>
        );
    }



    const isRejected = returnRequest.status === ReturnStatus.REJECTED;
    const isTerminal = isRejected || returnRequest.status === ReturnStatus.APPROVED;
    const showProgressTracker = !isRejected;
    const currentStatusIndex = getReturnStatusIndex(returnRequest.status);

    const parsedAddress = parseOrderAddress(returnRequest.order?.address);
    const orderItems = returnRequest.order?.items || [];

    console.log(isTerminal)

    return (
        <Layout>
            <div className="space-y-6 pb-20 animate-in fade-in duration-500">
                <MainHeading
                    title="Return Request"
                    description={`Requested on ${formatDateTime(returnRequest.createdAt)}`}
                    showBack
                    onBack={() => navigate(-1)}
                />

                {/* Progress Tracker */}
                {showProgressTracker && (
                    <ProgressTracker
                        title="Return Progress Tracker"
                        steps={returnSteps}
                        currentStatusIndex={currentStatusIndex}
                    />
                )}

                <div className="grid grid-cols-1 items-start lg:grid-cols-3 gap-6">

                    {/* LEFT Side: 2-column span */}
                    <div className="lg:col-span-2 space-y-6">

                        {/* Customer & Delivery Information */}
                        <SectionCard title="Customer & Delivery Address" icon={<User />}>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <DetailItem label="Full Name" value={returnRequest?.user?.fullName || '---'} icon={<User />} />
                                <DetailItem label="Phone" value={`${returnRequest?.user?.phone ?? '---'}`} icon={<Phone />} />
                                {/* {returnRequest?.user?.email && (
                                    <DetailItem label="Email" value={returnRequest?.user?.email} icon={<Mail />} />
                                )} */}
                                <DetailItem label="Delivery Type" value={<span className="capitalize">{formatStatus(returnRequest?.order?.deliveryType) || '---'}</span>} icon={<Truck />} />
                            </div>

                            {parsedAddress && (
                                <div className="mt-5 pt-5 border-t border-dashed">
                                    <p className="text-sm font-bold text-foreground mb-3 flex items-center gap-2">
                                        <MapPin className="w-4 h-4 text-destructive" /> Pickup / Delivery Address
                                    </p>
                                    <div className="bg-secondary/5 border border-secondary/10 rounded-xl p-4 space-y-1">
                                        {typeof parsedAddress === 'object' ? (
                                            <>
                                                <p className="font-semibold text-sm text-foreground">{parsedAddress.name}</p>
                                                <p className="text-sm text-muted-foreground">{parsedAddress.addressLine1}</p>
                                                {parsedAddress.addressLine2 && (
                                                    <p className="text-xs text-muted-foreground">{parsedAddress.addressLine2}</p>
                                                )}
                                                <p className="text-sm text-muted-foreground">
                                                    {[parsedAddress.city, parsedAddress.country].filter(Boolean).join(', ')}
                                                </p>
                                            </>
                                        ) : (
                                            <p className="text-sm text-foreground leading-relaxed">{parsedAddress}</p>
                                        )}
                                    </div>
                                </div>
                            )}
                        </SectionCard>

                        {/* Associated Order Details */}
                        {returnRequest.order && (
                            <SectionCard title="Associated Order Details" icon={<ShoppingBag />}>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <DetailItem label="Order ID" value={<span className="font-mono text-xs font-bold text-primary">#{returnRequest.order.orderId}</span>} icon={<Hash />} />
                                    <DetailItem label="Order Status" value={<StatusBadge status={returnRequest.order.status} />} icon={<Info />} />
                                    <DetailItem label="Payment Status" value={<span className="capitalize text-xs font-semibold px-2 py-0.5 rounded-full bg-secondary/10">{returnRequest.order.paymentStatus}</span>} icon={<Shield />} />
                                    <DetailItem label="Payment Method" value={<span className="capitalize text-xs font-semibold">{returnRequest.order.paymentMethod || '---'}</span>} icon={<Wallet />} />
                                    <DetailItem label="Placed At" value={formatDateTime(returnRequest.order.createdAt)} icon={<Calendar />} />
                                    <DetailItem label="Delivery Type" value={<span className="capitalize">{formatStatus(returnRequest.order.deliveryType) || '---'}</span>} icon={<Truck />} />
                                </div>

                                <div className="mt-5 pt-5 border-t border-dashed border-border/60 space-y-3">
                                    <h4 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">Financial Summary</h4>
                                    <div className="space-y-2">
                                        <div className="flex justify-between items-center text-xs">
                                            <span className="text-muted-foreground">Subtotal</span>
                                            <span className="font-semibold text-foreground">{formatRand(Number(returnRequest.order.totalAmount || 0))}</span>
                                        </div>
                                        {Number(returnRequest.order.deliveryFee || 0) > 0 && (
                                            <div className="flex justify-between items-center text-xs">
                                                <span className="text-muted-foreground">Delivery Fee</span>
                                                <span className="font-semibold text-foreground">{formatRand(Number(returnRequest.order.deliveryFee || 0))}</span>
                                            </div>
                                        )}
                                        {Number(returnRequest.order.taxAmount || 0) > 0 && (
                                            <div className="flex justify-between items-center text-xs">
                                                <span className="text-muted-foreground">Vat / Tax ({returnRequest.order.tax || 0}%)</span>
                                                <span className="font-semibold text-foreground">{formatRand(Number(returnRequest.order.taxAmount || 0))}</span>
                                            </div>
                                        )}
                                        {Number(returnRequest.order.plateformFree || 0) > 0 && (
                                            <div className="flex justify-between items-center text-xs">
                                                <span className="text-muted-foreground">Platform Fee</span>
                                                <span className="font-semibold text-foreground">{formatRand(Number(returnRequest.order.plateformFree || 0))}</span>
                                            </div>
                                        )}
                                        {Number(returnRequest.order.discountAmount || 0) > 0 && (
                                            <div className="flex justify-between items-center text-xs text-red-600">
                                                <span>Discount</span>
                                                <span className="font-semibold">-{formatRand(Number(returnRequest.order.discountAmount || 0))}</span>
                                            </div>
                                        )}
                                        {Number(returnRequest.order.walletPaidAmount || 0) > 0 && (
                                            <div className="flex justify-between items-center text-xs text-green-600">
                                                <span>Wallet Paid</span>
                                                <span className="font-semibold">{formatRand(Number(returnRequest.order.walletPaidAmount || 0))}</span>
                                            </div>
                                        )}
                                        {Number(returnRequest.order.pendingAmount || 0) > 0 && (
                                            <div className="flex justify-between items-center text-xs text-amber-600">
                                                <span>Pending Amount</span>
                                                <span className="font-semibold">{formatRand(Number(returnRequest.order.pendingAmount || 0))}</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between items-center text-sm pt-2 border-t border-dashed border-border/40 font-bold">
                                            <span className="text-foreground">Order Total</span>
                                            <span className="text-primary font-black">{formatRand(Number(returnRequest.order.finalAmount || 0))}</span>
                                        </div>
                                    </div>
                                </div>
                            </SectionCard>
                        )}

                        {/* Original Order Items Returned */}
                        <SectionCard
                            title="Items to Return"
                            icon={<Package />}
                            extra={
                                <span className="text-[10px] font-bold py-1 px-3 bg-primary/10 text-primary rounded-full">
                                    {returnRequest.returnItems?.length || 0} Items
                                </span>
                            }
                        >
                            {returnRequest.returnItems && returnRequest.returnItems.length > 0 ? (
                                <>
                                    <div className="overflow-x-auto rounded-xl border border-secondary/15 bg-secondary/5">
                                        <table className="w-full text-sm text-left">
                                            <thead className="bg-secondary/10 text-muted-foreground font-semibold">
                                                <tr>
                                                    {returnRequest.status !== ReturnStatus.APPROVED && returnRequest.status !== ReturnStatus.REJECTED && canEdit(RESOURCES.RETURNS) && (
                                                        <th className="px-4 py-3 border-b w-10"></th>
                                                    )}
                                                    <th className="px-4 py-3 border-b">Item Details</th>
                                                    <th className="px-4 py-3 border-b text-right">Qty</th>
                                                    <th className="px-4 py-3 border-b text-right">Price</th>
                                                    <th className="px-4 py-3 border-b text-right">Total</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-border/50">
                                                {returnRequest.returnItems.map((item, index) => {
                                                    const productName = item.orderItem.product?.name || 'Product';
                                                    const mediaUrl = item.orderItem.product?.media?.[0]?.url;

                                                    return (
                                                        <React.Fragment key={item.id || index}>
                                                            <tr className="hover:bg-secondary/10 transition-colors">
                                                                {returnRequest.status !== ReturnStatus.APPROVED && returnRequest.status !== ReturnStatus.REJECTED && canEdit(RESOURCES.RETURNS) && (
                                                                    <td className="px-4 py-3 w-10">
                                                                        <Checkbox
                                                                            checked={selectedReturnItems.includes(item.id)}
                                                                            onCheckedChange={(checked) => {
                                                                                if (checked) {
                                                                                    setSelectedReturnItems(prev => [...prev, item.id]);
                                                                                } else {
                                                                                    setSelectedReturnItems(prev => prev.filter(id => id !== item.id));
                                                                                }
                                                                            }}
                                                                            disabled={calculatingRefund}
                                                                            className="w-5 h-5 rounded-md border-primary/50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
                                                                        />
                                                                    </td>
                                                                )}
                                                                <td className="px-4 py-3">
                                                                    <div className="flex items-start gap-3">
                                                                        <div className="w-10 h-10 rounded-lg bg-secondary/10 flex-shrink-0 flex items-center justify-center overflow-hidden border border-secondary/20">
                                                                            {mediaUrl ? (
                                                                                <img
                                                                                    src={API_URL + "/" + mediaUrl}
                                                                                    alt={productName}
                                                                                    className="w-full h-full object-cover"
                                                                                    onError={(e) => {
                                                                                        (e.target as HTMLImageElement).style.display = 'none';
                                                                                        (e.target as HTMLImageElement).nextElementSibling?.classList.remove('hidden');
                                                                                    }}
                                                                                />
                                                                            ) : null}
                                                                            <Package className={`w-4 h-4 text-muted-foreground opacity-50 ${mediaUrl ? 'hidden' : ''}`} />
                                                                        </div>
                                                                        <div className="flex-1 min-w-0">
                                                                            <div className="flex items-center gap-2">
                                                                                <h4 className="font-semibold text-foreground truncate">
                                                                                    {productName}
                                                                                </h4>
                                                                                {returnRequest.status !== ReturnStatus.PENDING_REVIEW && item.status && (
                                                                                    <StatusBadge status={item.status} />
                                                                                )}
                                                                            </div>
                                                                            {(() => {
                                                                                const variant = item.orderItem?.variant || (item as any)?.variant;
                                                                                if (variant && variant.name) {
                                                                                    return (
                                                                                        <p className="text-[11px] font-bold text-primary uppercase tracking-wider mt-0.5">
                                                                                            Variant: {variant.name}
                                                                                            {variant.price ? ` ${formatRand(variant.price)}` : ''}
                                                                                        </p>
                                                                                    );
                                                                                }
                                                                                return null;
                                                                            })()}
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                                <td className="px-4 py-3 text-right font-medium">{item?.quantity}</td>
                                                                <td className="px-4 py-3 text-right">{(() => {
                                                                    const originalPrice = Number(item?.orderItem?.unitPrice);
                                                                    const discountedAmount = Number(item?.orderItem?.discountedProductAmount);
                                                                    const hasDiscount = discountedAmount > 0 && discountedAmount < originalPrice;
                                                                    const currentPrice = hasDiscount ? item?.orderItem?.totalItemPrice : originalPrice;

                                                                    return hasDiscount ? (
                                                                        <div className="flex flex-col items-end">
                                                                            <span className="text-xs text-muted-foreground line-through">{formatRand(originalPrice * Number(item?.orderItem?.quantity))}</span>
                                                                            <span className="font-medium text-green-600">{formatRand(currentPrice)}</span>
                                                                        </div>
                                                                    ) : (
                                                                        <span>{formatRand(currentPrice)}</span>
                                                                    );
                                                                })()}</td>
                                                                <td className="px-4 py-3 text-right font-bold text-primary">
                                                                    {(() => {
                                                                        const originalPrice = Number(item?.orderItem?.unitPrice);
                                                                        const discountedAmount = Number(item?.orderItem?.discountedProductAmount);
                                                                        const hasDiscount = discountedAmount > 0 && discountedAmount < originalPrice;
                                                                        const currentPrice = hasDiscount ? item?.orderItem?.totalItemPrice : item?.orderItem?.totalItemPrice;

                                                                        return formatRand(currentPrice);
                                                                    })()}
                                                                </td>
                                                            </tr>
                                                            {item.returnItemAddons && item.returnItemAddons.length > 0 && item.returnItemAddons.map((addon: any, idx: number) => {
                                                                const name = addon.orderItemAddon?.addon?.name || addon.addon?.name || addon.name || 'Addon';
                                                                const price = Number(addon.orderItemAddon?.price || addon.price || 0);
                                                                const qty = Number(addon.quantity || 0);
                                                                return (
                                                                    <tr key={addon.id || idx} className="bg-secondary/5/50">
                                                                        {returnRequest.status !== ReturnStatus.APPROVED && returnRequest.status !== ReturnStatus.REJECTED && canEdit(RESOURCES.RETURNS) && (
                                                                            <td className="px-4 py-2"></td>
                                                                        )}
                                                                        <td className="px-4 py-2 pl-12">
                                                                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                                                                                <Tag className="w-3 h-3" />
                                                                                <span>{name}</span>
                                                                            </div>
                                                                        </td>
                                                                        <td className="px-4 py-2 text-right text-xs text-muted-foreground">{qty}</td>
                                                                        <td className="px-4 py-2 text-right text-xs text-muted-foreground">+{formatRand(price)}</td>
                                                                        <td className="px-4 py-2 text-right text-xs font-medium text-muted-foreground">+{formatRand(price * qty)}</td>
                                                                    </tr>
                                                                );
                                                            })}
                                                        </React.Fragment>
                                                    );
                                                })}
                                            </tbody>
                                        </table>
                                    </div>
                                    {returnRequest.returnItems && returnRequest.returnItems.length > 0 && returnRequest.status !== ReturnStatus.APPROVED && returnRequest.status !== ReturnStatus.REJECTED && canEdit(RESOURCES.RETURNS) && (
                                        <div className="mt-4 pt-4 border-t border-dashed border-secondary/20 flex justify-end">
                                            <Button
                                                variant="primary"
                                                onClick={handleCalculateRefund}
                                                isLoading={calculatingRefund}
                                                disabled={selectedReturnItems.length === 0}
                                            >
                                                Calculate Refund
                                            </Button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="text-center py-8 text-muted-foreground">
                                    <Package className="w-10 h-10 mx-auto mb-2 opacity-40" />
                                    <p className="text-sm">No return items data available</p>
                                </div>
                            )}
                        </SectionCard>

                        {/* Return Proof Images */}
                        {returnRequest.media && returnRequest.media.length > 0 && (
                            <SectionCard title="Return Proof Gallery" icon={<Shield />}>
                                <DetailsGallery
                                    media={returnRequest.media}
                                    name="Return Proof"
                                    className="max-w-[300px]"
                                />
                            </SectionCard>
                        )}



                    </div>

                    {/* RIGHT Side: Summary & Quick Actions */}
                    <div className="space-y-6">

                        {/* Refund Summary Card */}
                        <SectionCard title="Refund Summary" icon={<Info />}>
                            <div className="space-y-3">
                                <DetailItem label="Original Order" value={<span className="font-mono text-xs text-primary">#{returnRequest.order?.orderId}</span>} icon={<Hash />} />
                                <DetailItem label="Action Type" value={<span className="capitalize">{<StatusBadge status={returnRequest.actionType || ""} />}</span>} icon={<Shield />} />
                                {/* <DetailItem label="Refund Method" value={<span className="capitalize">{returnRequest.refundMethod || '---'}</span>} icon={<Wallet />} /> */}
                                <DetailItem label="Reason for Return" value={returnRequest.reason || '---'} icon={<Info />} />
                                {returnRequest.customerNotes && (
                                    <DetailItem label="Customer Notes" value={returnRequest.customerNotes} icon={<Info />} />
                                )}
                                {returnRequest.rejectionReason && (
                                    <div className="bg-red-50 text-red-700 p-3 rounded-xl border border-red-100 mt-2 text-xs">
                                        <p className="font-bold flex items-center gap-1.5 mb-1 text-red-800">
                                            <XCircle className="w-3.5 h-3.5" /> Rejection Reason
                                        </p>
                                        <p>{returnRequest.rejectionReason}</p>
                                    </div>
                                )}
                            </div>

                            {returnRequest.refundAmount && <div className="mt-5 pt-4 border-t border-dashed space-y-2">
                                <div className="flex justify-between items-center">
                                    <span className="text-sm font-bold uppercase opacity-70">Refund Amount</span>
                                    <span className="text-2xl font-black text-primary">{formatRand(returnRequest.refundAmount)}</span>
                                </div>
                            </div>}
                        </SectionCard>

                        {/* Quick Actions Panel */}
                        {canEdit(RESOURCES.RETURNS) && !isTerminal && (
                            <SectionCard title="Quick Actions" icon={<CheckCircle2 />}>
                                <div className="space-y-3">

                                    {/* Action: Admin Review Phase */}
                                    {returnRequest.status === ReturnStatus.PENDING_REVIEW && (
                                        <>
                                            {returnRequest.refundAmount !== null && <Button
                                                variant="success" fullWidth
                                                leftIcon={<CheckCircle2 className="w-4 h-4" />}
                                                isLoading={actionLoading}
                                                onClick={() => {
                                                    if (returnRequest?.refundAmount) {
                                                        setShowRefundModal(true);
                                                    } else {
                                                        showToastNotification('danger', 'Refund amount is not calculated yet. Please calculate refund amount first.')
                                                    }
                                                }}
                                            >
                                                Approve Immediately
                                            </Button>
                                            }
                                            <Button
                                                variant="primary" fullWidth
                                                leftIcon={<Truck className="w-4 h-4" />}
                                                onClick={() => setShowAssignModal(true)}
                                            >
                                                Assign Driver
                                            </Button>
                                            <Button
                                                variant="destructive" fullWidth
                                                leftIcon={<XCircle className="w-4 h-4" />}
                                                disabled={actionLoading}
                                                onClick={() => setShowRejectModal(true)}
                                            >
                                                Reject Return
                                            </Button>
                                        </>
                                    )}

                                    {/* Action: Assign Driver for pickup */}
                                    {returnRequest.status === ReturnStatus.APPROVED && (
                                        <Button
                                            variant="primary" fullWidth
                                            leftIcon={<Truck className="w-4 h-4" />}
                                            onClick={() => setShowAssignModal(true)}
                                        >
                                            Assign Driver for Pickup
                                        </Button>
                                    )}

                                    {/* Transit State Manual Override Controls */}
                                    {returnRequest.status !== ReturnStatus.PENDING_REVIEW && returnRequest.status !== ReturnStatus.APPROVED && (
                                        <div className="space-y-3 border-t border-dashed pt-3 mt-2">
                                            <p className="text-xs font-bold text-muted-foreground uppercase tracking-wider flex items-center gap-1.5">
                                                <Truck className="w-3.5 h-3.5" /> Manual Transit Progress
                                            </p>
                                            <p className="text-[11px] text-muted-foreground leading-relaxed mb-2">
                                                Advance or modify the pickup and return state of this return request below:
                                            </p>

                                            <div className="grid grid-cols-1 gap-2">

                                                {returnRequest.status === ReturnStatus.RE_ARRIVED_AT_STORE && (
                                                    <div className="space-y-2 p-3 bg-primary/5 border border-primary/10 rounded-2xl text-left">
                                                        <label className="text-xs font-semibold text-foreground flex items-center gap-1.5">
                                                            <Lock className="w-3.5 h-3.5 text-primary animate-pulse" />
                                                            Enter Driver OTP
                                                        </label>
                                                        <p className="text-[10px] text-muted-foreground leading-relaxed">
                                                            Ask the driver for the 4-digit return verification OTP to confirm receipt back at the store.
                                                        </p>
                                                        <input
                                                            type="text"
                                                            maxLength={4}
                                                            className="w-full p-2.5 rounded-xl border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 text-center text-sm font-mono font-bold tracking-widest"
                                                            placeholder="••••"
                                                            value={verificationOtp}
                                                            onChange={(e) => setVerificationOtp(e.target.value.replace(/\D/g, ''))}
                                                        />
                                                        <Button
                                                            variant="primary"
                                                            fullWidth
                                                            size="sm"
                                                            className="mt-2"
                                                            leftIcon={<CheckCircle2 className="w-3.5 h-3.5" />}
                                                            isLoading={actionLoading}
                                                            disabled={verificationOtp.length !== 4}
                                                            onClick={() => handleStatusUpdate(ReturnStatus.RETURN_DELIVERED, { returnCode: verificationOtp, status: ReturnStatus.RETURN_DELIVERED })}
                                                        >
                                                            Verify OTP & Deliver
                                                        </Button>
                                                    </div>
                                                )}

                                                {returnRequest.status === ReturnStatus.RETURN_DELIVERED && (
                                                    <div className="space-y-2 p-3 bg-green-50 border border-green-200 rounded-2xl text-left">
                                                        <label className="text-xs font-semibold text-green-800 flex items-center gap-1.5">
                                                            <CheckCircle2 className="w-3.5 h-3.5 text-green-600 animate-bounce" />
                                                            Return Received at Store
                                                        </label>
                                                        <p className="text-[10px] text-green-700 leading-relaxed">
                                                            The returned item is successfully verified back in inventory. Proceed to process the customer's refund below.
                                                        </p>
                                                        <Button
                                                            variant="success"
                                                            fullWidth
                                                            size="sm"
                                                            className="mt-2"
                                                            leftIcon={<Wallet className="w-3.5 h-3.5" />}
                                                            isLoading={actionLoading}
                                                            onClick={() => {
                                                                if (returnRequest?.refundAmount) {
                                                                    setShowRefundModal(true);
                                                                } else {
                                                                    showToastNotification('danger', 'Refund amount is not calculated yet. Please calculate refund amount first.')
                                                                }
                                                            }}
                                                        >
                                                            Process Refund
                                                        </Button>
                                                    </div>
                                                )}

                                            </div>
                                        </div>
                                    )}

                                </div>
                            </SectionCard>
                        )}

                        {/* Associated Store / Store details */}
                        {returnRequest.returnItems?.[0]?.orderItem?.store && (
                            <SectionCard title="Assigned Store" icon={<Store />}>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <DetailItem label="Store Name" value={returnRequest.returnItems[0].orderItem.store.name} icon={<Store />} />
                                    <DetailItem label="Contact" value={returnRequest.returnItems[0].orderItem.store.contact} icon={<Phone />} />
                                    <DetailItem label="Store Address" value={returnRequest.returnItems[0].orderItem.store.address} icon={<MapPin />} />
                                </div>
                            </SectionCard>
                        )}

                        {/* Driver details card */}
                        {(returnRequest.driver || returnRequest.driverId) && (
                            <SectionCard title="Assigned Pickup Driver" icon={<Truck />}>
                                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                    <DetailItem label="Driver Name" value={returnRequest.driver?.fullName || '---'} icon={<User />} />
                                    <DetailItem label="Phone" value={returnRequest.driver?.phone || '---'} icon={<Phone />} />
                                    {returnRequest.driver?.email ? (
                                        <DetailItem label="Email" value={returnRequest.driver.email} icon={<Mail />} />
                                    ) : (
                                        <DetailItem label="Driver ID" value={<span className="font-mono text-xs">{returnRequest.driverId}</span>} icon={<Hash />} />
                                    )}
                                    <DetailItem label="Transit Stage" value={<StatusBadge status={returnRequest.status} />} icon={<Info />} />
                                </div>
                            </SectionCard>
                        )}

                    </div>

                </div>
            </div>

            {/* Rejection Modal */}
            <ConfirmationModal
                open={showRejectModal}
                onOpenChange={setShowRejectModal}
                onConfirm={() => handleStatusUpdate(
                    ReturnStatus.REJECTED,
                    { rejectionReason: rejectionReason }
                )}
                title="Reject Return Request"
                message="Are you sure you want to reject this customer's return request? Please provide a brief reason below."
                confirmText="Confirm Rejection"
                cancelText="Cancel"
                loading={actionLoading}
                type="danger"
                size='lg'
                confirmDisabled={!rejectionReason.trim()}
            >
                <div className="flex flex-col gap-2 mt-2">
                    <label className="text-xs font-semibold text-foreground">Rejection Reason</label>
                    <textarea
                        className="w-full p-3 rounded-xl border border-input bg-background focus:outline-none focus:ring-2 focus:ring-primary/20 text-xs resize-none"
                        rows={3}
                        placeholder="Provide a clear reason (e.g. Items show wear and tear, or Return policy timeframe expired)..."
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                    />
                </div>
            </ConfirmationModal>

            {/* Unified Process Refund Modal Component */}
            <ProcessRefundModal
                open={showRefundModal}
                onOpenChange={setShowRefundModal}
                onConfirm={() => handleStatusUpdate(ReturnStatus.APPROVED, {
                    actionType: returnRequest.status === ReturnStatus.PENDING_REVIEW ? 'instant_refund' : 'pickup_and_verify',
                    refundAmount: returnRequest.refundAmount,
                    approvedItemIds: selectedReturnItems
                })}
                status={returnRequest.status}
                requestedRefundAmount={returnRequest.refundAmount || ''}
                loading={actionLoading}
            />

            {/* Standard Return Driver Assignment Modal */}
            <AssignDriverModal
                open={showAssignModal}
                onOpenChange={setShowAssignModal}
                orderId={returnRequest?.order?.id || ''}
                isReturn={true}
                returnRequest={returnRequest}
                onSuccess={fetchReturnRequest}
            />
        </Layout>
    );
};
